/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable fp/no-mutation */
/* eslint-disable prefer-destructuring */
import { ReactElement, useRef, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import MuxPlayer from '@mux/mux-player-react'
import { throttle } from 'lodash'
import axios from 'src/utilities/http-client'
import { selectRoles, selectUserID } from 'src/v3/settings/settings.slice'
import { Alert, Snackbar } from '@mui/material'
import { useVttSubtitles } from 'src/v3/utilities/hooks/use-vtt-subtitles'
import {
  selectClassID,
  selectCurrentStep,
  selectStreamItems,
  stepCompleted,
  StreamStepVideo,
  selectProgressItem,
  videoProgressMade,
} from '../classroom.slice'

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let classID = useAppSelector(selectClassID)
  let userID = useAppSelector(selectUserID)
  let streamItems = useAppSelector(selectStreamItems)
  let currentStep = useAppSelector(selectCurrentStep)
  let video = streamItems[currentStep] as StreamStepVideo
  let progressItem = useAppSelector(selectProgressItem(currentStep))
  let roles = useAppSelector(selectRoles)
  let isAdmin = roles.includes('admin')
  let isCompleted = progressItem?.status === 'completed'
  let canSkip = isAdmin || isCompleted
  let [warning, setWarning] = useState<'none' | 'speed' | 'seek'>('none')

  // Track the last valid position
  let [lastValidPosition, setLastValidPosition] = useState(0)
  let playerRef = useRef(null)

  // Load VTT subtitles if available
  let { vttUrl } = useVttSubtitles(video?.id)

  let postProgress = (elapsedTime: number) => {
    axios.post('/api/v2/classroom/stream-event/video-progress', {
      userID,
      progress: elapsedTime,
      classID,
      type: 'video',
      videoId: video.id,
    })
  }

  let handleVideoComplete = (event: any) => {
    let player = event.target
    let videoDuration = player.duration
    let COMPLETION_THRESHOLD = 3 // Allow 3 seconds of grace
    if (!isCompleted) {
      // Only mark as complete if the last valid position was near the end
      if (lastValidPosition >= videoDuration - COMPLETION_THRESHOLD) {
        axios
          .post('/api/v2/classroom/stream-event/video', {
            userID,
            classID,
            type: 'video',
            videoId: video.id,
            status: 'completed',
          })
          .then(() => {
            dispatch(stepCompleted())
          })
      } else {
        // If they tried to skip to the end, reset to last valid position
        player.currentTime = lastValidPosition
      }
    }
  }

  let throttledProgress = useRef(
    throttle(
      (time: number) => {
        dispatch(videoProgressMade({ id: video.id, progress: time }))
        postProgress(time)
      },
      1000,
      { trailing: false },
    ),
  ).current

  let handleTimeUpdate = (event: any) => {
    let player = event.target
    let currentTime = player.currentTime
    let epsilon = 0.1
    let hasMeaningfulProgress =
      (progressItem?.progress ?? 0) > 0 &&
      Math.abs(currentTime - (progressItem?.progress || 0)) < epsilon

    if (!canSkip) {
      // Allow natural playback progression and backward skipping
      if (
        hasMeaningfulProgress ||
        currentTime <= lastValidPosition ||
        currentTime - lastValidPosition < 1
      ) {
        setLastValidPosition(currentTime)
        // This is a valid progression, post progress every 20 seconds
        let time = Math.floor(currentTime)
        if (time > 10 && time % 20 === 0) {
          throttledProgress(time)
        }
      } else {
        // If there's a forward jump, reset to last valid position
        player.currentTime = lastValidPosition
        setWarning('seek')
      }
    }
  }

  let handleRateChange = (event: any) => {
    let player = event.target
    if (!canSkip) {
      if (player.playbackRate !== 1.0) {
        player.playbackRate = 1.0
        setWarning('speed')
      }
    }
  }

  return (
    <div className="relative w-full aspect-video">
      <style>
        {!canSkip &&
          `
          mux-player {
            --seek-forward-button: none;
            --playback-rate-button: none;
            --pip-button: none;
            // For now we're allowing seek bar so a user can seek backwards
            // we may remove seek bar for the final version
            // --time-range: none;
          }
        `}
      </style>
      <MuxPlayer
        title={isAdmin ? `${video.name} (Admin Mode)` : video.name}
        ref={playerRef}
        src={video?.videoUrl.replace('mp4', 'm3u8')}
        accentColor="#f9ea4f"
        primaryColor="#6694E9"
        backwardSeekOffset={20}
        forwardSeekOffset={20}
        metadata={{
          video_id: video.id,
          video_title: video.name,
          viewer_user_id: userID,
        }}
        onTimeUpdate={handleTimeUpdate}
        onRateChange={handleRateChange}
        onEnded={handleVideoComplete}
        defaultHiddenCaptions={!vttUrl} // Only hide captions if we don't have a VTT file
        streamType="on-demand"
        startTime={progressItem?.progress || 0}
      >
        {vttUrl && (
          <track
            src={vttUrl}
            kind="subtitles"
            label="English"
            srcLang="en"
            default
          />
        )}
      </MuxPlayer>

      <Snackbar
        open={warning !== 'none'}
        autoHideDuration={6000}
        onClose={() => setWarning('none')}
      >
        <Alert
          onClose={() => setWarning('none')}
          severity="warning"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {warning === 'speed'
            ? 'Playback speed changes are not allowed'
            : 'Seeking forward is not allowed'}
        </Alert>
      </Snackbar>
    </div>
  )
}
